import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { C } from './styles/theme';
import ThemeToggle from './ThemeToggle';
import GlobalSearch from './GlobalSearch';

function useIsMobile() {
  const [m, setM] = useState(window.innerWidth <= 768);
  useEffect(() => { const h = () => setM(window.innerWidth <= 768); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []);
  return m;
}

const TabIcon = ({ d, size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{d}</svg>
);

const TennisBuzzLogo = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="64" height="64" rx="14" fill="#00205B" />
    <circle cx="32" cy="32" r="18" fill="#BDFF00" />
    <circle cx="32" cy="32" r="18" fill="url(#navLogoShine)" />
    <path d="M22 22c3.5 5 3.5 15 0 20" stroke="#4481f1" strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.7" />
    <path d="M42 22c-3.5 5-3.5 15 0 20" stroke="#4481f1" strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.7" />
    <defs><radialGradient id="navLogoShine" cx="38%" cy="30%" r="40%"><stop offset="0%" stopColor="rgba(255,255,255,0.3)" /><stop offset="100%" stopColor="rgba(0,0,0,0)" /></radialGradient></defs>
  </svg>
);

const S = {
  nav: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 18px 0 10px', height: 52, background: C.navy, color: C.white, position: 'sticky', top: 0, zIndex: 1000, boxShadow: '0 2px 16px rgba(0,32,91,0.3)' },
  logo: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, fontWeight: 800, color: C.white, textDecoration: 'none', letterSpacing: 2.5, fontFamily: "'Outfit', sans-serif" },
  burger: { display: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 4, flexDirection: 'column', gap: 4 },
  burgerLine: { display: 'block', width: 20, height: 2, background: C.white, borderRadius: 1 },
  links: { display: 'flex', alignItems: 'center', gap: 2 },
  link: { color: 'rgba(255,255,255,0.6)', textDecoration: 'none', padding: '6px 12px', borderRadius: 6, fontSize: 13, fontWeight: 500, transition: 'all 0.2s', whiteSpace: 'nowrap', letterSpacing: 0.2 },
  orgLink: { color: C.lime },
  liveDotNav: { display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: C.red, marginRight: 5, animation: 'pulse 1.5s infinite' },
  navDivider: { width: 1, height: 24, background: 'rgba(255,255,255,0.15)', margin: '0 14px' },
  loginBtn: { background: C.lime, color: C.navy, border: 'none', padding: '8px 20px', borderRadius: 6, fontWeight: 700, cursor: 'pointer', textDecoration: 'none', fontSize: 12, letterSpacing: 0.5, textTransform: 'uppercase' },
  logoutBtn: { background: 'transparent', color: 'rgba(255,255,255,0.5)', border: '1px solid rgba(255,255,255,0.2)', padding: '5px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 11, letterSpacing: 0.3 },
  searchBtn: { width: 24, height: 24, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', padding: 0, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', flexShrink: 0 },
  userArea: { display: 'flex', alignItems: 'center', gap: 8 },
  userChip: { display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px', background: 'rgba(255,255,255,0.08)', borderRadius: 20 },
  userDot: { color: C.lime, fontSize: 8 },
  userName: { color: 'rgba(255,255,255,0.8)', fontSize: 12, fontWeight: 500 },
  bottomBar: { position: 'fixed', bottom: 0, left: 0, right: 0, height: 60, background: C.navy, display: 'flex', justifyContent: 'space-around', alignItems: 'center', zIndex: 1000, boxShadow: '0 -2px 12px rgba(0,32,91,0.3)', borderTop: `1px solid ${C.navyLight}`, paddingBottom: 'env(safe-area-inset-bottom)' },
  tabItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', gap: 2, flex: 1, padding: '6px 0', position: 'relative', transition: 'color 0.2s' },
  tabLabel: { fontSize: 9, fontWeight: 600, letterSpacing: 0.3, fontFamily: "'Outfit', sans-serif" },
  tabLiveDot: { position: 'absolute', top: 4, right: '50%', marginRight: -14, width: 5, height: 5, borderRadius: '50%', background: C.red, animation: 'pulse 1.5s infinite' },
};

export default function Navbar({ dark, onToggleTheme }) {
  const { currentUser, logout } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const close = () => setOpen(false);
  const isOrg = currentUser?.role === 'organizer';
  const isAdmin = currentUser?.role === 'admin';
  const isMobile = useIsMobile();
  const active = p => location.pathname === p ? { color: C.lime, fontWeight: 600 } : {};
  const isActive = p => location.pathname === p || location.pathname.startsWith(p + '/');

  const tabs = [
    { path: '/', label: 'Home', icon: <TabIcon d={<><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></>} /> },
    { path: '/tournaments', label: 'Tourneys', icon: <TabIcon d={<><path d="M6 9H4.5a2.5 2.5 0 010-5H6"/><path d="M18 9h1.5a2.5 2.5 0 000-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/><path d="M18 2H6v7a6 6 0 0012 0V2z"/></>} /> },
    { path: '/matches', label: 'Matches', icon: <TabIcon d={<><circle cx="12" cy="12" r="2"/><path d="M16.24 7.76a6 6 0 010 8.49m-8.48-.01a6 6 0 010-8.49m11.31-2.82a10 10 0 010 14.14m-14.14 0a10 10 0 010-14.14"/></>} /> },
    { path: currentUser ? (isAdmin ? '/admin' : isOrg ? '/organizer' : '/profile') : '/login', label: currentUser ? (isAdmin ? 'Admin' : isOrg ? 'Events' : 'Profile') : 'Sign In', icon: <TabIcon d={currentUser ? <><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4-4v2"/><circle cx="12" cy="7" r="4"/></> : <><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></>} /> },
  ];

  return (
    <>
      <nav style={{ ...S.nav, background: 'var(--nav-bg)' }}>
        <Link to="/" style={S.logo}><TennisBuzzLogo /><span>TennisBuzz</span></Link>
        {!isMobile && (
          <>
            <button style={S.burger} onClick={() => setOpen(!open)}><span style={S.burgerLine} /><span style={S.burgerLine} /><span style={S.burgerLine} /></button>
            <div style={{ ...S.links, ...(open ? { display: 'flex' } : {}) }}>
              <Link to="/" style={{ ...S.link, ...active('/') }} onClick={close}>Home</Link>
              <Link to="/tournaments" style={{ ...S.link, ...active('/tournaments') }} onClick={close}>Tournaments</Link>
              <Link to="/matches" style={{ ...S.link, ...active('/matches') }} onClick={close}>Matches</Link>
              <Link to="/players" style={{ ...S.link, ...active('/players') }} onClick={close}>Players</Link>
              {isOrg && <Link to="/organizer" style={{ ...S.link, ...S.orgLink, ...active('/organizer') }} onClick={close}>My Events</Link>}
              {isAdmin && <Link to="/admin" style={{ ...S.link, ...S.orgLink, ...active('/admin') }} onClick={close}>Admin Panel</Link>}
              <div style={S.navDivider} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button onClick={() => setSearchOpen(true)} style={S.searchBtn} title="Search"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg></button>
                <ThemeToggle dark={dark} onToggle={onToggleTheme} />
              </div>
              <div style={{ width: 12 }} />
              {currentUser ? (
                <div style={S.userArea}>
                  {currentUser.role === 'player' && <Link to="/profile" style={{ ...S.link, ...active('/profile') }} onClick={close}>My Profile</Link>}
                  {currentUser.role === 'admin' && <Link to="/admin" style={{ ...S.link, ...active('/admin') }} onClick={close}>Admin Panel</Link>}
                  <div style={S.userChip}><span style={S.userDot}>{isAdmin ? '★' : isOrg ? '◆' : '●'}</span><span style={S.userName}>{currentUser.name}</span></div>
                  <button onClick={() => { logout(); navigate('/'); close(); }} style={S.logoutBtn}>Sign Out</button>
                </div>
              ) : (
                <Link to="/login" style={S.loginBtn} onClick={close}>Sign In</Link>
              )}
            </div>
          </>
        )}
        {isMobile && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={() => setSearchOpen(true)} style={S.searchBtn} title="Search"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg></button>
            <ThemeToggle dark={dark} onToggle={onToggleTheme} />
            {currentUser && <button onClick={() => { logout(); navigate('/'); }} style={{ ...S.logoutBtn, padding: '5px 10px', fontSize: 10 }}>Sign Out</button>}
          </div>
        )}
      </nav>
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
      {isMobile && (
        <div style={S.bottomBar}>
          {tabs.map(t => {
            const act = isActive(t.path);
            const showDot = t.path === '/matches';
            return (
              <Link key={t.path + t.label} to={t.path} style={{ ...S.tabItem, color: act ? C.lime : 'rgba(255,255,255,0.45)' }}>
                {t.icon}<span style={S.tabLabel}>{t.label}</span>
                {showDot && <span style={S.tabLiveDot} />}
              </Link>
            );
          })}
        </div>
      )}
    </>
  );
}
